/**
 * Finans (Tahsilat / Ödeme) tür kayıt defteri — TAHSILAT_ODEME.md §1/§3.
 *
 * TEK KAYNAK: liste ekranı, detay ekranı, rota tanımları ve sol menü buradan beslenir.
 * Yeni bir ödeme türü eklemek (tür listesi GENİŞLETİLEBİLİRDİR) üç adımdır:
 *   1. `METHOD`e enum değeri + `TYPES`a bir kayıt ekle (ikon, renk, enstrüman alanları),
 *   2. AppRoutes.js'e liste + detay rotası,
 *   3. NavMenu.js `financeMenuItems`e menü kalemi (ve migration ile DB menüsü).
 * Ekranların geri kalanı otomatik uyum sağlar.
 *
 * ── RENK DİSİPLİNİ (§8) ────────────────────────────────────────────────────────────────────────
 * Renk TEK kaynaktan (`TONE`) gelir ve YALNIZ içerik alanında görünür: sayfa başlığı rozeti,
 * liste TÜR kolonu pill'i, 4. stat kartının şeridi. Sol menü sade/nötr kalır. Renk tek başına
 * anlam taşımaz — her rozette ikon + metin birlikte durur (renk körlüğü / erişilebilirlik).
 */

// ── Backend enum'larıyla birebir (DataScope.Entities/Enums/FinanceEnums.cs) ────────────────────
export const DIRECTION = {
  collection: 1,   // Alınan Tahsilat  — borç azalır (−)
  payment: 2,      // Ödeme            — borç artar  (+)
  debitEntry: 3,   // Borç Girişi      — borç artar  (+)
  creditEntry: 4,  // Alacak Girişi    — borç azalır (−)
};

export const METHOD = {
  cash: 1,
  creditCard: 2,
  cheque: 3,
  note: 4,
  dbs: 5,
  manual: 6,
  bankTransfer: 7,
};

export const STATUS = { active: 1, cancelled: 2 };

/** Çek/Senet portföy durumu — FinancePortfolioStatus. */
export const PORTFOLIO_STATUS = [
  { value: 1, label: 'Portföyde' },
  { value: 2, label: 'Ciro edildi / verildi' },
  { value: 3, label: 'Bankaya verildi' },
  { value: 4, label: 'Tahsil edildi / ödendi' },
];

/** Manuel giriş sebebi — FinanceTransaction.ManualReason. */
export const MANUAL_REASONS = [
  { value: 'price_diff', label: 'Fiyat farkı' },
  { value: 'return_adj', label: 'İade düzeltmesi' },
  { value: 'opening', label: 'Açılış bakiyesi' },
  { value: 'other', label: 'Diğer' },
];

export const CURRENCIES = [
  { value: 'TRY', label: 'TRY ₺' },
  { value: 'USD', label: 'USD $' },
  { value: 'EUR', label: 'EUR €' },
];

// ── Tür ikonları — uygulamanın inline SVG stiliyle (DetailShell.Icon ile aynı çizgi/kalınlık) ──
const PATHS = {
  cash: <><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /><line x1="6" y1="12" x2="6.01" y2="12" /><line x1="18" y1="12" x2="18.01" y2="12" /></>,
  bankTransfer: <><polyline points="7 7 3 11 7 15" /><line x1="3" y1="11" x2="14" y2="11" /><polyline points="17 9 21 13 17 17" /><line x1="21" y1="13" x2="10" y2="13" /></>,
  creditCard: <><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /><line x1="6" y1="15" x2="10" y2="15" /></>,
  cheque: <><rect x="3" y="4" width="18" height="16" rx="2" /><polyline points="7 12 10 15 17 8" /></>,
  note: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" /></>,
  dbs: <><polyline points="3 10 12 4 21 10" /><line x1="5" y1="10" x2="5" y2="19" /><line x1="12" y1="10" x2="12" y2="19" /><line x1="19" y1="10" x2="19" y2="19" /><line x1="3" y1="20" x2="21" y2="20" /></>,
  debit: <><line x1="17" y1="7" x2="7" y2="17" /><polyline points="17 15 7 17 9 7" /></>,
  credit: <><line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 9 17 7 15 17" /></>,
};

export function TypeIcon({ name, size = 18 }) {
  const p = PATHS[name];
  if (!p) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{p}</svg>
  );
}

/**
 * Renk tonları — `financeScreens.css` içindeki `.fin-tone-*` sınıflarıyla eşleşir.
 * Ham hex burada TUTULMAZ; CSS token'ları temaya (açık/koyu) göre çözülür.
 */
export const TONE = {
  teal: 'teal', blue: 'blue', lblue: 'lblue', purple: 'purple',
  amber: 'amber', green: 'green', red: 'red',
};

/**
 * Enstrüman alanları — TAHSILAT_ODEME.md §3 tablosu. Türe göre GÖSTER/GİZLE değil,
 * DOM'dan ÇIKAR (§8: gizlenen alan yanlış veriyle kaydedilmesin).
 * Alan adları FinanceTransaction kolonlarıyla birebir.
 */
const FIELDS = {
  cash: ['amount', 'currencyCode', 'description'],
  bankTransfer: ['bankName', 'chequeBranch', 'bankAccountNo', 'amount', 'description'],
  creditCard: ['bankName', 'cardLast4', 'installmentCount', 'authCode', 'amount', 'description'],
  cheque: ['chequeNo', 'bankName', 'chequeBranch', 'drawerName', 'dueDate', 'amount', 'portfolioStatus', 'description'],
  note: ['chequeNo', 'drawerName', 'dueDate', 'amount', 'portfolioStatus', 'description'],
  dbs: ['bankName', 'dbsReference', 'dueDate', 'amount', 'description'],
  manual: ['manualReason', 'amount', 'description'],
};

/**
 * Alan etiketleri. Bazıları yöne göre değişir (§3 dipnot 1): Çek'te Keşideci → Lehtar,
 * Senet'te Borçlu → Lehtar. `labelOf` bunu çözer.
 */
const LABELS = {
  amount: 'Tutar',
  currencyCode: 'Para Birimi',
  description: 'Açıklama',
  bankName: 'Banka',
  chequeBranch: 'Şube',
  bankAccountNo: 'Hesap Numarası / IBAN',
  cardLast4: 'Kart No (Son 4 Hane)',
  installmentCount: 'Taksit Sayısı',
  authCode: 'Provizyon / Onay Kodu',
  chequeNo: 'Çek No',
  drawerName: 'Keşideci',
  dueDate: 'Vade Tarihi',
  portfolioStatus: 'Portföy Durumu',
  dbsReference: 'DBS Referans No',
  manualReason: 'Sebep',
};

/** Zorunlu enstrüman alanları (§3) — `*` ve kaydetme doğrulaması buradan okunur. */
const REQUIRED = {
  cash: ['amount'],
  bankTransfer: ['bankName', 'bankAccountNo', 'amount'],
  creditCard: ['bankName', 'cardLast4', 'amount'],
  cheque: ['chequeNo', 'bankName', 'dueDate', 'amount'],
  note: ['chequeNo', 'dueDate', 'amount'],
  dbs: ['bankName', 'dbsReference', 'amount'],
  manual: ['manualReason', 'amount'],
};

/**
 * Tür kataloğu. `group` sol menü grubunu (§1), `direction` cari işaretini belirler.
 * `unit` stat kartlarındaki adet birimidir (belge / işlem / çek / senet / talimat / kayıt).
 */
export const TYPES = [
  // ── Alınan Tahsilatlar ────────────────────────────────────────────────────────────────────
  {
    key: 'collection/cash', group: 'collection', slug: 'cash', method: METHOD.cash,
    direction: DIRECTION.collection, icon: 'cash', tone: TONE.teal, unit: 'belge',
    typeLabel: 'Nakit', title: 'Nakit Tahsilat',
    subtitle: 'Müşteriden nakit alınan tahsilatlar.', instrument: 'cash',
  },
  {
    key: 'collection/bank-transfer', group: 'collection', slug: 'bank-transfer', method: METHOD.bankTransfer,
    direction: DIRECTION.collection, icon: 'bankTransfer', tone: TONE.blue, unit: 'işlem',
    typeLabel: 'Havale/EFT', title: 'Havale/EFT Tahsilat',
    subtitle: 'Banka havalesi / EFT ile alınan tahsilatlar.', instrument: 'bankTransfer',
  },
  {
    key: 'collection/credit-card', group: 'collection', slug: 'credit-card', method: METHOD.creditCard,
    direction: DIRECTION.collection, icon: 'creditCard', tone: TONE.lblue, unit: 'işlem',
    typeLabel: 'Kredi Kartı', title: 'Kredi Kartı Tahsilat',
    subtitle: 'Kredi kartı / POS ile alınan tahsilatlar.', instrument: 'creditCard',
  },
  {
    key: 'collection/cheque', group: 'collection', slug: 'cheque', method: METHOD.cheque,
    direction: DIRECTION.collection, icon: 'cheque', tone: TONE.purple, unit: 'çek',
    typeLabel: 'Çek', title: 'Çek Tahsilat',
    subtitle: 'Müşteriden alınan çekler.', instrument: 'cheque', hasDueDate: true,
  },
  {
    key: 'collection/promissory-note', group: 'collection', slug: 'promissory-note', method: METHOD.note,
    direction: DIRECTION.collection, icon: 'note', tone: TONE.amber, unit: 'senet',
    typeLabel: 'Senet', title: 'Senet Tahsilat',
    subtitle: 'Müşteriden alınan senetler.', instrument: 'note', hasDueDate: true,
  },
  {
    key: 'collection/dbs', group: 'collection', slug: 'dbs', method: METHOD.dbs,
    direction: DIRECTION.collection, icon: 'dbs', tone: TONE.green, unit: 'talimat',
    typeLabel: 'DBS', title: 'DBS Tahsilat',
    subtitle: 'Doğrudan Borçlandırma Sistemi tahsilatları.', instrument: 'dbs',
  },
  {
    // #827 — Alacak Girişi Ödemelerim'den BURAYA taşındı. Gerekçe yön: tahsilat grubundaki her
    // kalem müşterinin borcunu AZALTIR (direction 1 ve 4), ödeme grubundaki her kalem ARTIRIR
    // (2 ve 3). Eski yerleşim bu ikisini ters koymuştu.
    key: 'collection/credit-entry', group: 'collection', slug: 'credit-entry', method: METHOD.manual,
    direction: DIRECTION.creditEntry, icon: 'credit', tone: TONE.green, unit: 'kayıt',
    typeLabel: 'Alacak Girişi', title: 'Alacak Girişi',
    subtitle: 'Müşteri hesabına manuel alacak girişi.', instrument: 'manual',
  },

  // ── Ödemelerim (tahsilatın simetrik kopyası — aynı alan/dizayn, yön ters) ──────────────────
  {
    key: 'payment/cash', group: 'payment', slug: 'cash', method: METHOD.cash,
    direction: DIRECTION.payment, icon: 'cash', tone: TONE.teal, unit: 'belge',
    typeLabel: 'Nakit', title: 'Nakit Ödeme',
    subtitle: 'Müşteriye nakit yapılan ödemeler.', instrument: 'cash',
  },
  {
    key: 'payment/bank-transfer', group: 'payment', slug: 'bank-transfer', method: METHOD.bankTransfer,
    direction: DIRECTION.payment, icon: 'bankTransfer', tone: TONE.blue, unit: 'işlem',
    typeLabel: 'Havale/EFT', title: 'Havale/EFT Ödeme',
    subtitle: 'Banka havalesi / EFT ile yapılan ödemeler.', instrument: 'bankTransfer',
  },
  {
    key: 'payment/credit-card', group: 'payment', slug: 'credit-card', method: METHOD.creditCard,
    direction: DIRECTION.payment, icon: 'creditCard', tone: TONE.lblue, unit: 'işlem',
    typeLabel: 'Kredi Kartı', title: 'Kredi Kartı Ödeme',
    subtitle: 'Kredi kartı ile yapılan ödemeler.', instrument: 'creditCard',
  },
  {
    key: 'payment/cheque', group: 'payment', slug: 'cheque', method: METHOD.cheque,
    direction: DIRECTION.payment, icon: 'cheque', tone: TONE.purple, unit: 'çek',
    typeLabel: 'Çek', title: 'Çek Ödeme',
    subtitle: 'Müşteriye verilen çekler.', instrument: 'cheque', hasDueDate: true,
  },
  {
    key: 'payment/promissory-note', group: 'payment', slug: 'promissory-note', method: METHOD.note,
    direction: DIRECTION.payment, icon: 'note', tone: TONE.amber, unit: 'senet',
    typeLabel: 'Senet', title: 'Senet Ödeme',
    subtitle: 'Müşteriye verilen senetler.', instrument: 'note', hasDueDate: true,
  },
  {
    key: 'payment/dbs', group: 'payment', slug: 'dbs', method: METHOD.dbs,
    direction: DIRECTION.payment, icon: 'dbs', tone: TONE.green, unit: 'talimat',
    typeLabel: 'DBS', title: 'DBS Ödeme',
    subtitle: 'DBS ile yapılan ödemeler.', instrument: 'dbs',
  },
  {
    // #827 — Borç Girişi Alınan Tahsilatlar'dan BURAYA taşındı (yukarıdaki gerekçenin simetriği).
    key: 'payment/debit-entry', group: 'payment', slug: 'debit-entry', method: METHOD.manual,
    direction: DIRECTION.debitEntry, icon: 'debit', tone: TONE.red, unit: 'kayıt',
    typeLabel: 'Borç Girişi', title: 'Borç Girişi',
    subtitle: 'Müşteri hesabına manuel borç girişi.', instrument: 'manual',
  },
];

/** Grup meta — §3: Tahsilat yeşil/gelen, Ödeme amber/giden. Makbuz ≠ Dekont. */
export const GROUP = {
  collection: {
    section: 'Finans · Alınan Tahsilatlar',
    word: 'Tahsilat',
    heroTone: 'green',
    receiptLabel: 'Makbuz',
    routeBase: '/finance/collections',
    counterpartyLabel: { cheque: 'Keşideci', note: 'Borçlu' },
  },
  payment: {
    section: 'Finans · Ödemelerim',
    word: 'Ödeme',
    heroTone: 'amber',
    receiptLabel: 'Dekont',
    routeBase: '/finance/payments',
    // §3 dipnot 1: ödeme tarafında karşı rol Lehtar'a döner.
    counterpartyLabel: { cheque: 'Lehtar', note: 'Lehtar' },
  },
};

const BY_KEY = new Map(TYPES.map((t) => [t.key, t]));

/** `group`+`slug` → tür kaydı. Rotalar bu ikiliyi prop olarak geçirir. */
export function getType(group, slug) {
  return BY_KEY.get(`${group}/${slug}`) || null;
}

/** Bir türün liste rotası (detay/yeni bundan türetilir). */
export function listRouteOf(type) {
  return `${GROUP[type.group].routeBase}/${type.slug}`;
}

/**
 * Bir FinanceTransaction kaydının (yön + yöntem) ait olduğu ekranın liste rotası.
 *
 * Rotayı YALNIZ `method`ten tahmin eden eşlemeler kırılgandır: yeni bir yöntem eklendiğinde
 * eksik kalıp sessizce yanlış ekranı açar (Havale/EFT'de bir kez oldu), #827 gibi bir grup
 * değişikliğinde de bayatlar. Burada kayıt defteri sorgulanır; eşleşme yoksa `null` döner ki
 * çağıran "bağlantı verme" kararını bilinçli alsın, yanlış ekrana götürmesin.
 */
export function listRouteFor(direction, method) {
  const t = TYPES.find((x) => x.direction === Number(direction) && x.method === Number(method));
  return t ? listRouteOf(t) : null;
}

/** Enstrüman çerçevesinde çizilecek alan adları (sırasıyla). */
export function instrumentFields(type) {
  return FIELDS[type.instrument] || [];
}

export function isRequiredField(type, field) {
  return (REQUIRED[type.instrument] || []).includes(field);
}

/**
 * Alan etiketi — karşı taraf rolü yöne ve türe göre değişir (§3 dipnot 1):
 * Çek: Keşideci → Lehtar · Senet: Borçlu → Lehtar.
 */
export function labelOf(type, field) {
  if (field === 'drawerName') {
    const map = GROUP[type.group].counterpartyLabel;
    return map[type.instrument] || LABELS.drawerName;
  }
  if (field === 'chequeNo' && type.instrument === 'note') return 'Senet No';
  if (field === 'bankName' && type.instrument === 'creditCard') return 'Banka (POS)';
  if (field === 'dueDate' && type.instrument === 'dbs') return 'Talimat Vadesi';
  return LABELS[field] || field;
}

/** Liste "Vade" kolonu YALNIZ Çek/Senet'te render edilir (§8) — boş `—` bile değil. */
export function showsDueDateColumn(type) {
  return !!type.hasDueDate;
}
